/**
 * PostalSlipAssignDialog - Dialog per assegnazione manuale bollettini a fatture
 *
 * Simile all'AssignmentDialog per i bonifici, permette:
 * - Ricerca cliente
 * - Ricerca fattura diretta
 * - Selezione multipla fatture
 * - Registrazione acconto
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  TextField,
  Chip,
  Alert,
  IconButton,
  Checkbox,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { postalSlipsApi, customersApi, invoicesApi } from '../../services/api';
import { invalidatePostalAccounting, paymentAmountsByInvoice } from '../../utils/postalAccounting';
import { formatNuf } from '../../utils/formatNuf';
import { formatCustomerName } from '../../utils/formatCustomerName';
import { useAuth } from '../../context/AuthContext';
import { confidenceLabel, parseSuggestions } from './PostalSlipMatchSummary';
import PostalSlipImagePreview from './PostalSlipImagePreview';

// Formatta valuta
const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '€ 0,00';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(num);
};

export default function PostalSlipAssignDialog({
  open,
  onClose,
  slip,
  queryClient,
  onShowImage,
  editMode = false
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { tenant } = useAuth();
  const [accountingRevision, setAccountingRevision] = useState(null);
  const [accountingError, setAccountingError] = useState('');
  const [accountingMissing, setAccountingMissing] = useState(false);
  const [retainedAdvances, setRetainedAdvances] = useState([]);
  const [keepAdvances, setKeepAdvances] = useState(true);
  const operationRef = useRef(null);
  const sourceRef = useRef(null);
  const invoiceLoadRef = useRef(0);
  const [reviewSlip, setReviewSlip] = useState(null);
  const [imageReview, setImageReview] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState('');
  const [corrections, setCorrections] = useState({ numeroFattura: '', codiceCliente: '', nomeCliente: '' });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [isAcconto, setIsAcconto] = useState(false);
  const [accontoAmount, setAccontoAmount] = useState('');
  const [invoiceSearchText, setInvoiceSearchText] = useState('');
  const [directInvoiceResults, setDirectInvoiceResults] = useState([]);
  const [searchMode, setSearchMode] = useState('customer');
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null); // Fattura selezionata dalla ricerca
  const [showRemainingWarning, setShowRemainingWarning] = useState(false); // Dialog conferma rimanenza
  const [pendingAssignment, setPendingAssignment] = useState(null); // Dati assegnazione in attesa di conferma

  // Filtri e ordinamento
  const [orderBy, setOrderBy] = useState('default'); // 'default' = insolute prima (data desc), poi pagate (data desc)
  const [orderDir, setOrderDir] = useState('desc');
  const [filters, setFilters] = useState({
    nuf: '',
    stato: 'all',
    importoMin: '',
    importoMax: ''
  });

  // In edit mode, l'intero importo del bollettino è disponibile per la riassegnazione
  // In modalità normale, sottrai l'importo già assegnato
  const importoDisponibile = editMode
    ? parseFloat(slip?.importo || 0)
    : parseFloat(slip?.importo || 0) - parseFloat(slip?.importo_assegnato || 0);

  // Parse suggerimenti dal bollettino
  const suggestions = useMemo(() => parseSuggestions((reviewSlip || slip)?.suggestions), [reviewSlip, slip]);

  // Reset quando il dialog si apre/chiude
  useEffect(() => {
    let cancelled = false;
    invoiceLoadRef.current++;
    setImageLoaded(false);
    setImageError('');
    const loadExistingPayments = async () => {
      if (slip?.id) {
        setLoadingInvoices(true);
        setAccountingError('');
        setAccountingRevision(null);
        try {
          const response = await postalSlipsApi.getAssignments(slip.id);
          if (cancelled) return;
          const source = response.data.data;
          sourceRef.current = source;
          setReviewSlip(source);
          setImageReview(source.image_review || { available: false, error: 'Scansione non disponibile: recuperare l’originale.' });
          const extracted = source.analysis?.extraction || {};
          const corrected = source.analysis?.corrections || {};
          setCorrections({ numeroFattura: corrected.numeroFattura ?? extracted.numeroFattura ?? source.ocr_numero_fattura ?? '',
            codiceCliente: corrected.codiceCliente ?? extracted.codiceCliente ?? source.ocr_codice_cliente ?? '',
            nomeCliente: corrected.nomeCliente ?? extracted.nomeCliente ?? source.ocr_nome_cliente ?? '' });
          setRetainedAdvances(source.advances);
          setAccountingRevision(source.revision);
          setAccountingMissing(source.accounting_missing);
          const customerId = source.payments[0]?.customer_id || source.advances[0]?.customer_id || slip.customer_id;
          if (customerId) {
            const custRes = await customersApi.getById(customerId);
            if (cancelled) return;
            const customer = custRes.data?.data || custRes.data;
            setSelectedCustomer(customer);
            if (editMode) await loadCustomerInvoicesForEdit(customer.id, slip.id, source);
            else await loadCustomerInvoices(customer.id, source.invoice_id);
            if (editMode && source.advances.length && !source.payments.length) {
              setIsAcconto(true);
              setAccontoAmount(String(source.advances.reduce((sum, item) => sum + Number(item.ac_paga_imp), 0)));
            }
          }
        } catch (err) {
          if (!cancelled) setAccountingError(err.response?.data?.message || 'Impossibile verificare le assegnazioni del bollettino');
        } finally { if (!cancelled) setLoadingInvoices(false); }
      }
    };

    if (open) {
      loadExistingPayments();
    } else {
      // Reset
      setAccountingRevision(null);
      setAccountingError('');
      setAccountingMissing(false);
      setRetainedAdvances([]);
      setKeepAdvances(true);
      operationRef.current = null;
      sourceRef.current = null;
      setReviewSlip(null);
      setImageReview(null);
      setImageLoaded(false);
      setImageError('');
      setSelectedCustomer(null);
      setInvoiceOptions([]);
      setCustomerOptions([]);
      setIsAcconto(false);
      setAccontoAmount('');
      setInvoiceSearchText('');
      setDirectInvoiceResults([]);
      setSelectedInvoiceId(null);
      setOrderBy('default');
      setOrderDir('desc');
      setFilters({ nuf: '', stato: 'all', importoMin: '', importoMax: '' });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slip?.id, editMode]);

  // Mutation assegnazione
  const assignMutation = useMutation({
    mutationFn: (data) => {
      if (!imageLoaded || !imageReview?.available || imageError) throw new Error('Caricare la scansione del bollettino prima di salvare');
      const payload = { ...data, expected_revision: accountingRevision, visual_review_confirmed: true,
        reviewed_image_hash: imageReview.version };
      const signature = JSON.stringify(payload);
      if (operationRef.current?.signature !== signature) {
        operationRef.current = { signature, key: window.crypto.randomUUID() };
      }
      return postalSlipsApi.assign(slip.id, { ...payload, request_key: operationRef.current.key });
    },
    onSuccess: (response) => {
      const updated = response.data?.data;
      if (updated) queryClient.setQueriesData({ queryKey: ['postal-slips'] }, old => !Array.isArray(old?.data) ? old
        : { ...old, data: old.data.map(row => row.id === updated.id ? updated : row) });
      invalidatePostalAccounting(queryClient);
      onClose();
    },
  });

  const correctionMutation = useMutation({
    mutationFn: () => postalSlipsApi.update(slip.id, { ocr_numero_fattura: corrections.numeroFattura,
      ocr_codice_cliente: corrections.codiceCliente, ocr_nome_cliente: corrections.nomeCliente }),
    onSuccess: response => { setReviewSlip(response.data.data); invalidatePostalAccounting(queryClient); }
  });

  // Ricerca cliente
  const handleCustomerSearch = async (searchText) => {
    if (searchText.length < 2) return;
    try {
      const res = await customersApi.getAll({ search: searchText, limit: 20 });
      setCustomerOptions(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Ricerca fattura diretta
  const handleInvoiceSearch = async (searchText) => {
    setInvoiceSearchText(searchText);
    if (searchText.length < 1) {
      setDirectInvoiceResults([]);
      return;
    }
    try {
      console.log('[PostalSlipAssign] Searching invoices with:', searchText);
      const res = await invoicesApi.getAll({ search: searchText, limit: 30 });
      console.log('[PostalSlipAssign] API Response:', res.data);
      const results = (res.data?.data || []).map(inv => ({
        ...inv,
        saldo_residuo: parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0),
        is_open: (parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0)) > 0.01
      })).sort((a, b) => {
        if (a.is_open && !b.is_open) return -1;
        if (!a.is_open && b.is_open) return 1;
        return new Date(b.f_emission) - new Date(a.f_emission);
      });
      setDirectInvoiceResults(results);
    } catch (err) {
      console.error(err);
    }
  };

  // Seleziona fattura dalla ricerca diretta
  const handleDirectInvoiceSelect = async (invoice) => {
    if (invoice.customer_id) {
      try {
        const res = await customersApi.getById(invoice.customer_id);
        // La risposta è { success: true, data: {...} }
        const customer = res.data?.data || res.data;
        console.log('[PostalSlipAssign] Selected customer from invoice:', customer?.id, customer?.ragione_sociale);
        setSelectedCustomer(customer);
        setSelectedInvoiceId(invoice.id); // Mantieni l'ID della fattura selezionata
        // Carica fatture e pre-seleziona quella cercata in un unico update
        await loadCustomerInvoices(customer.id, invoice.id);
        // Nascondi i risultati della ricerca
        setDirectInvoiceResults([]);
        // NON resettare invoiceSearchText - così l'utente vede cosa ha cercato
      } catch (err) {
        console.error(err);
      }
    }
  };

  // Carica fatture cliente (con opzione di pre-selezionare una fattura)
  const loadCustomerInvoices = async (customerId, invoiceIdToSelect = null) => {
    const requestId = ++invoiceLoadRef.current;
    setLoadingInvoices(true);
    console.log('[PostalSlipAssign] Loading invoices for customer:', customerId, 'preselect invoice:', invoiceIdToSelect);
    try {
      const res = await invoicesApi.getAll({ customer_id: customerId, limit: 100 });
      const invs = (res.data?.data || []).map(inv => {
        const saldoResiduo = parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0);
        // Confronto con conversione a numero per sicurezza
        const shouldSelect = invoiceIdToSelect && Number(inv.id) === Number(invoiceIdToSelect) && saldoResiduo > 0.01;
        if (shouldSelect) {
          console.log('[PostalSlipAssign] Pre-selecting invoice:', inv.id, formatNuf(inv.sezionale, inv.nuf), 'residuo:', saldoResiduo);
        }
        return {
          ...inv,
          saldo_residuo: saldoResiduo,
          selected: shouldSelect,
          importo_assegnato: shouldSelect ? Math.min(saldoResiduo, importoDisponibile) : 0,
          is_open: saldoResiduo > 0.01
        };
      });
      invs.sort((a, b) => {
        if (a.is_open && !b.is_open) return -1;
        if (!a.is_open && b.is_open) return 1;
        return new Date(b.f_emission) - new Date(a.f_emission);
      });
      console.log('[PostalSlipAssign] Loaded', invs.length, 'invoices, selected:', invs.filter(i => i.selected).length);
      if (requestId === invoiceLoadRef.current) setInvoiceOptions(invs);
    } catch (err) {
      console.error(err);
    } finally {
      if (requestId === invoiceLoadRef.current) setLoadingInvoices(false);
    }
  };

  // Carica fatture per modalità modifica - cerca pagamenti esistenti da questo bollettino
  const loadCustomerInvoicesForEdit = async (customerId, slipId, source = sourceRef.current) => {
    const requestId = ++invoiceLoadRef.current;
    setLoadingInvoices(true);
    console.log('[PostalSlipAssign] Loading invoices for EDIT mode, customer:', customerId, 'slip:', slipId);
    try {
      // Carica fatture del cliente
      const res = await invoicesApi.getAll({ customer_id: customerId, limit: 100 });

      // Server returns only records owned by this exact slip, across all pages.
      const slipPayments = source?.payments || [];
      const paidInvoiceIds = slipPayments.map(p => Number(p.invoice_id));
      const paidAmounts = paymentAmountsByInvoice(slipPayments);
      const available = res.data?.data || [];
      const missingIds = [...new Set(paidInvoiceIds)].filter(id => !available.some(inv => Number(inv.id) === id));
      const additional = await Promise.all(missingIds.map(id => invoicesApi.getById(id).then(response => response.data.data)));
      const invs = [...available, ...additional].map(inv => {
        const saldoResiduo = parseFloat(inv.f_totale) - parseFloat(inv.f_paga_imp || 0);
        const isPaidByThisSlip = paidInvoiceIds.includes(Number(inv.id));
        // In edit mode, consideriamo "open" anche le fatture già pagate da questo bollettino
        // perché l'utente potrebbe voler deselezionarle
        const isOpen = saldoResiduo > 0.01 || isPaidByThisSlip;
        // Se era pagata da questo bollettino, il saldo residuo effettivo include quell'importo
        const effectiveResiduo = isPaidByThisSlip ? saldoResiduo + paidAmounts[inv.id] : saldoResiduo;

        return {
          ...inv,
          saldo_residuo: effectiveResiduo,
          selected: isPaidByThisSlip,
          importo_assegnato: isPaidByThisSlip ? paidAmounts[inv.id] : 0,
          is_open: isOpen,
          was_paid_by_slip: isPaidByThisSlip, // Flag per sapere che era già pagata
          original_payment_amount: isPaidByThisSlip ? paidAmounts[inv.id] : 0
        };
      });

      invs.sort((a, b) => {
        // Prima le selezionate (già pagate da questo bollettino)
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        // Poi le aperte
        if (a.is_open && !b.is_open) return -1;
        if (!a.is_open && b.is_open) return 1;
        return new Date(b.f_emission) - new Date(a.f_emission);
      });

      console.log('[PostalSlipAssign] EDIT mode: Loaded', invs.length, 'invoices, pre-selected:', invs.filter(i => i.selected).length);
      if (requestId === invoiceLoadRef.current) setInvoiceOptions(invs);
    } catch (err) {
      console.error('[PostalSlipAssign] Error loading invoices for edit:', err);
      if (requestId === invoiceLoadRef.current) setAccountingError(err.response?.data?.message || 'Impossibile caricare tutte le fatture già assegnate');
    } finally {
      if (requestId === invoiceLoadRef.current) setLoadingInvoices(false);
    }
  };

  const handleCustomerSelect = async (customer) => {
    setSelectedCustomer(customer);
    if (customer) {
      await loadCustomerInvoices(customer.id);
    }
  };

  const toggleInvoice = (invId) => {
    setInvoiceOptions(prev => prev.map(inv => {
      if (inv.id === invId) {
        if (!inv.is_open) return inv;
        const newSelected = !inv.selected;
        return {
          ...inv,
          selected: newSelected,
          importo_assegnato: newSelected ? Math.min(inv.saldo_residuo, importoDisponibile) : 0
        };
      }
      return inv;
    }));
  };

  const handleSave = () => {
    if (!reviewReady) return;
    const toAssign = [];

    if (isAcconto && accontoAmount) {
      toAssign.push({
        customer_id: selectedCustomer.id,
        tipo: 'acconto',
        amount: parseFloat(accontoAmount)
      });
    } else {
      invoiceOptions.filter(i => i.selected).forEach(inv => {
        toAssign.push({
          invoice_id: inv.id,
          customer_id: selectedCustomer.id,
          tipo: 'fattura',
          amount: parseFloat(inv.importo_assegnato)
        });
      });
      if (editMode && keepAdvances) retainedAdvances.forEach(advance => toAssign.push({
        tipo: 'acconto', customer_id: advance.customer_id, amount: Number(advance.ac_paga_imp)
      }));
    }

    // In edit mode, permetti di salvare anche senza assegnazioni (rimuove tutte le assegnazioni)
    if (toAssign.length === 0 && editMode) {
      assignMutation.mutate({ assignments: [], editMode });
      return;
    }

    if (toAssign.length > 0) {
      // Calcola la rimanenza
      const totAssegnato = toAssign.reduce((sum, t) => sum + t.amount, 0);
      const rimanenza = importoDisponibile - totAssegnato;

      // Se c'è rimanenza significativa (> 0.01€), mostra warning
      if (rimanenza > 0.01) {
        setPendingAssignment(toAssign);
        setShowRemainingWarning(true);
      } else {
        // Nessuna rimanenza, procedi direttamente
        assignMutation.mutate({ assignments: toAssign, editMode });
      }
    }
  };

  // Conferma assegnazione con rimanenza
  const handleConfirmWithRemaining = () => {
    if (pendingAssignment && reviewReady) {
      assignMutation.mutate({ assignments: pendingAssignment, editMode });
    }
    setShowRemainingWarning(false);
    setPendingAssignment(null);
  };

  // Annulla assegnazione con rimanenza
  const handleCancelRemaining = () => {
    setShowRemainingWarning(false);
    setPendingAssignment(null);
  };

  const totaleSelezionato = isAcconto ? Number(accontoAmount || 0) : invoiceOptions
    .filter(i => i.selected)
    .reduce((sum, i) => sum + parseFloat(i.importo_assegnato || 0),
      editMode && !isAcconto && keepAdvances ? retainedAdvances.reduce((sum, item) => sum + Number(item.ac_paga_imp), 0) : 0);

  const openInvoicesCount = invoiceOptions.filter(i => i.is_open).length;
  const paidInvoicesCount = invoiceOptions.filter(i => !i.is_open).length;

  // Ordinamento
  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDir(column === 'is_open' ? 'desc' : 'asc');
    }
  };

  // Fatture filtrate
  const filteredInvoices = useMemo(() => {
    let result = [...invoiceOptions];

    if (filters.nuf) {
      result = result.filter(inv =>
        formatNuf(inv.sezionale, inv.nuf).toLowerCase().includes(filters.nuf.toLowerCase())
      );
    }
    if (filters.stato === 'open') {
      result = result.filter(inv => inv.is_open);
    } else if (filters.stato === 'paid') {
      result = result.filter(inv => !inv.is_open);
    }
    if (filters.importoMin) {
      const min = parseFloat(filters.importoMin);
      result = result.filter(inv => inv.saldo_residuo >= min);
    }
    if (filters.importoMax) {
      const max = parseFloat(filters.importoMax);
      result = result.filter(inv => inv.saldo_residuo <= max);
    }

    result.sort((a, b) => {
      let cmp = 0;
      switch (orderBy) {
        case 'default':
          // Ordinamento di default: prima insolute (dalla più recente), poi pagate (dalla più recente)
          if (a.is_open && !b.is_open) return -1;
          if (!a.is_open && b.is_open) return 1;
          // Stesso stato: ordina per data decrescente (più recente prima)
          return new Date(b.f_emission) - new Date(a.f_emission);
        case 'nuf':
          cmp = formatNuf(a.sezionale, a.nuf).localeCompare(formatNuf(b.sezionale, b.nuf));
          break;
        case 'f_emission':
          cmp = new Date(a.f_emission) - new Date(b.f_emission);
          break;
        case 'is_open':
          cmp = (a.is_open === b.is_open) ? 0 : a.is_open ? -1 : 1;
          break;
        case 'f_totale':
          cmp = parseFloat(a.f_totale) - parseFloat(b.f_totale);
          break;
        case 'saldo_residuo':
          cmp = a.saldo_residuo - b.saldo_residuo;
          break;
        default:
          cmp = 0;
      }
      return orderDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [invoiceOptions, filters, orderBy, orderDir]);

  if (!slip) return null;

  // URL immagine bollettino con tenant per multi-tenant
  const tenantSlug = tenant?.slug || '';
  const imageUrl = imageReview?.available
    ? `/api/postal-slips/${slip.id}/image?tenant=${encodeURIComponent(tenantSlug)}&v=${imageReview.version}`
    : null;
  const reviewReady = imageLoaded && imageReview?.available && !imageError && accountingRevision !== null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { maxWidth: 1760 } }}>
      <DialogTitle>
        {editMode ? 'Modifica Assegnazione Bollettino' : 'Assegna Bollettino Postale'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Dati, scansione e ricerca affiancati sui desktop. */}
        <Box sx={{ display: 'grid', gap: 2, mb: 2, alignItems: 'start', gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: 'minmax(0, 0.8fr) minmax(0, 1.2fr)',
          lg: 'minmax(0, 0.8fr) minmax(0, 1.15fr) minmax(0, 1.15fr)'
        } }}>
          {/* Colonna sinistra - Informazioni */}
          <Paper sx={{ p: 2, minWidth: 0, bgcolor: 'grey.50', overflowWrap: 'anywhere' }}>
            <Typography variant="subtitle1" gutterBottom color="primary" fontWeight="bold">
              📋 Dati Bollettino
            </Typography>

            {/* Dati da CSV */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Da file CSV:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>Data operazione:</strong> {slip.data_operazione ? format(new Date(slip.data_operazione), 'dd/MM/yyyy', { locale: it }) : '-'}
                </Typography>
                {slip.data_accredito && (
                  <Typography variant="body2">
                    <strong>Data accredito:</strong> {format(new Date(slip.data_accredito), 'dd/MM/yyyy', { locale: it })}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, typography: 'body2' }}>
                  <strong>Importo:</strong> <Chip label={formatCurrency(slip.importo)} color="success" size="small" />
                </Box>
                {slip.codice_riferimento && (
                  <Typography variant="body2">
                    <strong>Codice riferimento:</strong> {slip.codice_riferimento}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Dati da OCR */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Letto dal bollettino (può contenere errori):
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {slip.ocr_causale && (
                  <Typography variant="body2">
                    <strong>Causale:</strong> {slip.ocr_causale}
                  </Typography>
                )}
                {slip.ocr_numero_fattura && (
                  <Typography variant="body2">
                    <strong>N. Fattura:</strong> {slip.ocr_numero_fattura}
                  </Typography>
                )}
                {slip.ocr_nome_cliente && (
                  <Typography variant="body2">
                    <strong>Nome cliente:</strong> {slip.ocr_nome_cliente}
                  </Typography>
                )}
                {slip.ocr_codice_cliente && (
                  <Typography variant="body2">
                    <strong>Codice cliente:</strong> {slip.ocr_codice_cliente}
                  </Typography>
                )}
                {slip.ocr_confidence != null && (
                  <Typography variant="body2">
                    <strong>Confidence OCR:</strong> {Math.round(slip.ocr_confidence)}%
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Importo disponibile */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Chip
                label={`Importo disponibile: ${formatCurrency(importoDisponibile)}`}
                color="warning"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          </Paper>

          <PostalSlipImagePreview slip={reviewSlip || slip} imageUrl={imageUrl} imageReview={imageReview} accountingError={accountingError}
            comparison={{ customer: selectedCustomer, invoices: invoiceOptions.filter(invoice => invoice.selected), amount: slip.importo }}
            onImageLoad={() => { setImageLoaded(true); setImageError(''); }}
            onImageError={message => { setImageLoaded(false); setImageError(message); }} />
          <Box sx={{ minWidth: 0, gridColumn: { xs: 'auto', md: '1 / -1', lg: 'auto' } }}>
            {!editMode && <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Correggi i dati letti prima di cercare le fatture</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[['numeroFattura','Fattura letta'],['codiceCliente','Codice cliente letto'],['nomeCliente','Nome versante letto']].map(([key,label]) =>
                  <TextField key={key} size="small" label={label} value={corrections[key]} onChange={e => setCorrections(c => ({ ...c, [key]: e.target.value }))}
                    sx={{ flex: '1 1 140px', minWidth: 0 }} />)}
                <Button onClick={() => correctionMutation.mutate()} disabled={correctionMutation.isPending || !imageLoaded}>Ricalcola suggerimenti</Button>
              </Box>
              {correctionMutation.isError && <Alert severity="error">{correctionMutation.error?.response?.data?.message || 'Impossibile aggiornare i suggerimenti'}</Alert>}
            </Paper>}

            {/* Suggerimenti match (se presenti) */}
            {!editMode && suggestions.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Suggerimenti da verificare:</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  {suggestions.slice(0, 3).map((s, idx) => (
                    <Chip
                      key={idx}
                      icon={s.tipo === 'fattura' ? <ReceiptIcon /> : <PersonIcon />}
                      label={s.tipo === 'fattura'
                        ? `Fatt. ${s.invoiceNum} · affidabilità ${confidenceLabel(s).toLowerCase()}`
                        : `${s.customerName} - Acconto`
                      }
                      size="small"
                      color={s.warnings?.length ? 'warning' : 'info'}
                      onClick={() => {
                        if (s.invoiceId && s.customerId) handleDirectInvoiceSelect({ id: s.invoiceId, customer_id: s.customerId });
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Alert>
            )}

            {/* Toggle modalità ricerca */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant={searchMode === 'customer' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setSearchMode('customer')}
              >
                Cerca Cliente
              </Button>
              <Button
                variant={searchMode === 'invoice' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setSearchMode('invoice')}
              >
                Cerca Fattura
              </Button>
            </Box>

            {searchMode === 'customer' ? (
              <Autocomplete
                options={customerOptions}
                getOptionLabel={(opt) => `${opt.cod_cli} - ${formatCustomerName(opt, '')}`}
                value={selectedCustomer}
                onChange={(e, v) => handleCustomerSelect(v)}
                onInputChange={(e, v) => handleCustomerSearch(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    placeholder="Cerca per nome, codice cliente..."
                    sx={{ mb: 2 }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">
                        <strong>{option.cod_cli}</strong> - {formatCustomerName(option, '')}
                      </Typography>
                      {option.citta && (
                        <Typography variant="caption" color="text.secondary">
                          {option.citta} ({option.provincia})
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
            ) : (
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Cerca Fattura"
                  placeholder="Numero fattura o importo"
                  value={invoiceSearchText}
                  onChange={(e) => handleInvoiceSearch(e.target.value)}
                  sx={{ mb: 1 }}
                />
                {directInvoiceResults.length > 0 && (
                  <Paper sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <List dense>
                      {directInvoiceResults.map(inv => {
                        const saldo = inv.saldo_residuo;
                        return (
                          <ListItem
                            key={inv.id}
                            button
                            onClick={() => handleDirectInvoiceSelect(inv)}
                            sx={{
                              borderLeft: saldo > 0.01 ? '4px solid' : 'none',
                              borderColor: 'error.main'
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2">
                                    <strong>{formatNuf(inv.sezionale, inv.nuf)}</strong> - {formatCustomerName(inv.customer, 'N/D')}}
                                  </Typography>
                                  <Typography variant="body2" color={saldo > 0.01 ? 'error' : 'success.main'}>
                                    {saldo > 0.01 ? `Residuo: ${formatCurrency(saldo)}` : 'Pagata'}
                                  </Typography>
                                </Box>
                              }
                              secondary={`Data: ${inv.f_emission ? format(new Date(inv.f_emission), 'dd/MM/yyyy') : '-'} - Totale: ${formatCurrency(inv.f_totale)}`}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Paper>
                )}
              </Box>
            )}

            {selectedCustomer && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Cliente:</strong> {selectedCustomer.cod_cli} - {formatCustomerName(selectedCustomer, '')}
                </Alert>
            )}
          </Box>
        </Box>

        {selectedCustomer && (
          <>
            <Box sx={{ mb: 2 }}>
              <Button
                variant={!isAcconto ? 'contained' : 'outlined'}
                onClick={() => setIsAcconto(false)}
                sx={{ mr: 1 }}
              >
                Assegna a Fatture
              </Button>
              <Button
                variant={isAcconto ? 'contained' : 'outlined'}
                onClick={() => setIsAcconto(true)}
              >
                Registra come Acconto
              </Button>
            </Box>

            {isAcconto ? (
              <TextField
                label="Importo Acconto"
                type="number"
                fullWidth
                value={accontoAmount}
                onChange={(e) => setAccontoAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                helperText={`Massimo: ${formatCurrency(importoDisponibile)}`}
              />
            ) : (
              <>
                {loadingInvoices ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Riepilogo */}
                    <Paper
                      elevation={3}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: totaleSelezionato > importoDisponibile ? 'error.main' :
                                 Math.abs(totaleSelezionato - importoDisponibile) < 0.01 ? 'success.main' : 'primary.main',
                        color: 'white'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>DISPONIBILE</Typography>
                          <Typography variant="h6" fontWeight="bold">{formatCurrency(importoDisponibile)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', flex: 1, borderLeft: '1px solid rgba(255,255,255,0.3)', borderRight: '1px solid rgba(255,255,255,0.3)' }}>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>ASSEGNATO</Typography>
                          <Typography variant="h6" fontWeight="bold">{formatCurrency(totaleSelezionato)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>RIMANENTE</Typography>
                          <Typography variant="h6" fontWeight="bold">{formatCurrency(importoDisponibile - totaleSelezionato)}</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    <Typography variant="subtitle2" gutterBottom>
                      Fatture: {openInvoicesCount} aperte, {paidInvoicesCount} pagate
                    </Typography>

                    {/* Filtri */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        placeholder="Filtra fattura..."
                        value={filters.nuf}
                        onChange={(e) => setFilters(f => ({ ...f, nuf: e.target.value }))}
                        sx={{ width: 130 }}
                        InputProps={{
                          startAdornment: <SearchIcon sx={{ fontSize: 16, color: 'grey.500', mr: 0.5 }} />
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={filters.stato}
                          onChange={(e) => setFilters(f => ({ ...f, stato: e.target.value }))}
                        >
                          <MenuItem value="all">Tutte</MenuItem>
                          <MenuItem value="open">Da pagare</MenuItem>
                          <MenuItem value="paid">Pagate</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        placeholder="Min €"
                        type="number"
                        value={filters.importoMin}
                        onChange={(e) => setFilters(f => ({ ...f, importoMin: e.target.value }))}
                        sx={{ width: 80 }}
                      />
                      <TextField
                        size="small"
                        placeholder="Max €"
                        type="number"
                        value={filters.importoMax}
                        onChange={(e) => setFilters(f => ({ ...f, importoMax: e.target.value }))}
                        sx={{ width: 80 }}
                      />
                    </Box>

                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell sortDirection={orderBy === 'nuf' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'nuf'}
                                direction={orderBy === 'nuf' ? orderDir : 'asc'}
                                onClick={() => handleSort('nuf')}
                              >
                                Fattura
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === 'f_emission' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'f_emission'}
                                direction={orderBy === 'f_emission' ? orderDir : 'asc'}
                                onClick={() => handleSort('f_emission')}
                              >
                                Data
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === 'is_open' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'is_open'}
                                direction={orderBy === 'is_open' ? orderDir : 'asc'}
                                onClick={() => handleSort('is_open')}
                              >
                                Stato
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sortDirection={orderBy === 'f_totale' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'f_totale'}
                                direction={orderBy === 'f_totale' ? orderDir : 'asc'}
                                onClick={() => handleSort('f_totale')}
                              >
                                Totale
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sortDirection={orderBy === 'saldo_residuo' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'saldo_residuo'}
                                direction={orderBy === 'saldo_residuo' ? orderDir : 'asc'}
                                onClick={() => handleSort('saldo_residuo')}
                              >
                                Residuo
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === 'f_paga_dt' ? orderDir : false}>
                              <TableSortLabel
                                active={orderBy === 'f_paga_dt'}
                                direction={orderBy === 'f_paga_dt' ? orderDir : 'asc'}
                                onClick={() => handleSort('f_paga_dt')}
                              >
                                Data Pag.
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Tipo Pag.</TableCell>
                            <TableCell align="right">Da assegnare</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredInvoices.map(inv => (
                            <TableRow
                              key={inv.id}
                              hover
                              sx={{
                                bgcolor: inv.id === selectedInvoiceId ? 'primary.light' :
                                         inv.is_open ? 'transparent' : 'grey.100',
                                opacity: inv.is_open ? 1 : 0.7,
                                border: inv.id === selectedInvoiceId ? '2px solid' : 'none',
                                borderColor: 'primary.main'
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={!!inv.selected}
                                  onChange={() => toggleInvoice(inv.id)}
                                  disabled={!inv.is_open}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography
                                    component="a"
                                    href={`/invoices/${inv.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      color: 'primary.main',
                                      textDecoration: 'none',
                                      fontWeight: 500,
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                  >
                                    {formatNuf(inv.sezionale, inv.nuf)}
                                  </Typography>
                                  <OpenInNewIcon sx={{ fontSize: 14, color: 'grey.500' }} />
                                </Box>
                              </TableCell>
                              <TableCell>{format(new Date(inv.f_emission), 'dd/MM/yy')}</TableCell>
                              <TableCell>
                                {inv.is_open ? (
                                  <Chip label="Da pagare" size="small" color="error" variant="outlined" />
                                ) : (
                                  <Chip label="Pagata" size="small" color="success" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(inv.f_totale)}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight="bold"
                                  color={inv.is_open ? 'error.main' : 'success.main'}
                                >
                                  {formatCurrency(inv.saldo_residuo)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {inv.f_paga_dt ? format(new Date(inv.f_paga_dt), 'dd/MM/yy') : '-'}
                              </TableCell>
                              <TableCell>
                                {inv.ultimo_pagamento_modalita || (inv.f_paga_imp > 0 ? 'N/D' : '-')}
                              </TableCell>
                              <TableCell align="right">
                                {inv.selected && (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={inv.importo_assegnato}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value) || 0;
                                      setInvoiceOptions(prev => prev.map(i =>
                                        i.id === inv.id
                                          ? { ...i, importo_assegnato: Math.min(val, i.saldo_residuo) }
                                          : i
                                      ));
                                    }}
                                    sx={{ width: 100 }}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">€</InputAdornment>
                                    }}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </>
        )}

        {accountingError && <Alert severity="error" sx={{ mb: 2 }}>{accountingError}</Alert>}
        {editMode && !isAcconto && retainedAdvances.length > 0 && <Box sx={{ mb: 2 }}>
          <Checkbox checked={keepAdvances} onChange={event => setKeepAdvances(event.target.checked)} inputProps={{ 'aria-label': 'Mantieni gli acconti già registrati' }} />
          Mantieni gli acconti già registrati ({formatCurrency(retainedAdvances.reduce((sum, item) => sum + Number(item.ac_paga_imp), 0))})
        </Box>}
        {accountingMissing && <Alert severity="warning" sx={{ mb: 2 }}>Questo bollettino risulta confermato ma non ha pagamenti registrati. Verifica le fatture e gli importi prima di salvare una nuova assegnazione.</Alert>}
        {assignMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {assignMutation.error?.response?.data?.message || assignMutation.error?.message}
          </Alert>
        )}
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2">Riepilogo da registrare</Typography>
          <Typography variant="body2">Cliente: {formatCustomerName(selectedCustomer, 'Da scegliere')}</Typography>
          {invoiceOptions.filter(i => i.selected).map(i => <Typography key={i.id} variant="body2">
            Fattura {formatNuf(i.sezionale, i.nuf)} · {formatCurrency(i.importo_assegnato)}
          </Typography>)}
          {isAcconto && <Typography variant="body2">Acconto: {formatCurrency(accontoAmount)}</Typography>}
          {editMode && keepAdvances && !isAcconto && retainedAdvances.length > 0 && <Typography variant="body2">Acconti conservati: {formatCurrency(retainedAdvances.reduce((sum,a) => sum + Number(a.ac_paga_imp),0))}</Typography>}
          <Typography variant="body2">Totale assegnato: {formatCurrency(totaleSelezionato)} · Rimanenza: {formatCurrency(importoDisponibile - totaleSelezionato)}</Typography>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        {/* In edit mode, se nessuna fattura selezionata, mostra pulsante per rimuovere assegnazione */}
        {editMode && !isAcconto && totaleSelezionato === 0 ? (
          <Button
            variant="contained"
            color="warning"
            onClick={handleSave}
            disabled={!reviewReady || correctionMutation.isPending || assignMutation.isPending || loadingInvoices || Boolean(accountingError) || (editMode && accountingRevision === null) || !selectedCustomer}
          >
            {assignMutation.isPending ? <CircularProgress size={20} /> : 'Rimuovi Assegnazione'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !reviewReady || correctionMutation.isPending || assignMutation.isPending || loadingInvoices || Boolean(accountingError) || (editMode && accountingRevision === null) ||
              !selectedCustomer ||
              (isAcconto ? !accontoAmount : totaleSelezionato === 0)
            }
          >
            {assignMutation.isPending ? <CircularProgress size={20} /> : editMode ? 'Salva Modifiche' : 'Assegna'}
          </Button>
        )}
      </DialogActions>

      {/* Dialog conferma rimanenza */}
      <Dialog open={showRemainingWarning} onClose={handleCancelRemaining} maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          ⚠️ Attenzione: Rimanenza Positiva
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            L'importo assegnato è inferiore all'importo del bollettino.
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Importo bollettino:</strong> {formatCurrency(importoDisponibile)}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Importo assegnato:</strong> {formatCurrency(pendingAssignment?.reduce((sum, t) => sum + t.amount, 0) || 0)}
            </Typography>
            <Typography variant="body1" color="warning.main" fontWeight="bold">
              <strong>Rimanenza:</strong> {formatCurrency(importoDisponibile - (pendingAssignment?.reduce((sum, t) => sum + t.amount, 0) || 0))}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Vuoi procedere comunque con l'assegnazione parziale? La rimanenza non verrà assegnata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemaining} color="inherit">
            Torna Indietro
          </Button>
          <Button
            onClick={handleConfirmWithRemaining}
            variant="contained"
            color="warning"
          >
            Procedi Comunque
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
